import React, {
  ComponentType,
  useEffect,
  useState
} from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import {
  Container,
  Header,
  Icon,
  Image,
  Menu,
  Segment,
  Grid
} from 'semantic-ui-react'
import {
  NotificationContainer,
  NotificationManager
} from 'react-notifications'
import 'react-notifications/lib/notifications.css'
// Local
import {
  doResetPassword,
  validatePassresetToken
} from '../../action/user'
import { PAGES, shouldRenderReskin2025 } from '../../constant'
import UserPasswordChange from './userPasswordChange'
import isNotNil from '../common/truthy'
import { formatLongDateTime } from '../common/dateTimeUtils'
import If from '../common/if'
import ReveleerHeaderLogo from '../logos/ReveleerHeaderLogo'
import LockVersionTwoLocked from '../icons/LockVersionTwoLocked'
// Styling
import './index.css'

const SafeSegment = Segment as unknown as ComponentType<any>
const SafeGrid = Grid as unknown as ComponentType<any>
const SafeHeader = Header as unknown as ComponentType<any>
const SafeImage = Image as unknown as ComponentType<any>

type PasswordChange = {
  token: string
  userid: string
  email: string
  password: string
  confirmPassword: string
}

type ValidateResponse = {
  status: number
  data: {
    expiration: string
    timezone: string
  }
}

type ResetProps = {
  match: {
    params: {
      token: string
      userid: string
    }
  }
  history: {
    push: (path: string) => void
  }
  doResetPassword: (
    passwordChange: any,
    callback: (data: any) => void
  ) => void
}

function Reset(props: ResetProps): JSX.Element {
  const {
    match,
    history,
    doResetPassword: doResetPasswordAction
  } = props
  const {
    token,
    userid
  } = match.params

  const [
    expirationTime,
    setExpirationTime
  ] = useState<Date | null>(null)
  const [
    timezone,
    setTimezone
  ] = useState<string | null>(null)
  const [
    passwordChange,
    setPasswordChange
  ] = useState<PasswordChange>({
    token,
    userid,
    email: '',
    password: '',
    confirmPassword: ''
  })
  const [
    errors,
    setErrors
  ] = useState<string[] | null>(null)
  const [
    isExpiredToken,
    setIsExpiredToken
  ] = useState(false)

  useEffect(() => {
    if (isNotNil(userid)) {
      const handleValidateResponse = (response: ValidateResponse | false) => {
        if (response === false || response.status !== 200) {
          setIsExpiredToken(true)
        } else {
          const millis = Number(response.data.expiration)
          setExpirationTime(new Date(millis))
          setTimezone(response.data.timezone)
        }
      }
      validatePassresetToken(token, userid, handleValidateResponse)
    }
  }, [])

  const onSavePasswordChange = (updatedPasswordChange: PasswordChange) => {
    doResetPasswordAction(updatedPasswordChange, (data: any) => {
      const resetPasswordChange = {
        ...updatedPasswordChange,
        password: '',
        confirmPassword: ''
      }
      if (data.status === 'OK') {
        NotificationManager.success('Your password has been reset.')
        setPasswordChange(resetPasswordChange)
        setErrors(null)
        // pause to allow the notification to be read
        // and then jump to the login page
        setTimeout(() => history.push(PAGES.LOGIN), 2500)
      } else {
        setPasswordChange(resetPasswordChange)
        setErrors(data.errors as string[])
      }
    })
  }

  if (isExpiredToken) {
    return (
      <div>
        <Menu borderless>
          <Menu.Item>
            <If isTrue={ !shouldRenderReskin2025 }>
              <SafeImage
                size="small"
                src="/asset/image/curation-logo-no-tag.png"
              />
            </If>
            <If isTrue={ shouldRenderReskin2025 }>
              <ReveleerHeaderLogo />
            </If>
          </Menu.Item>
        </Menu>
        <div><span>Sorry, your link has expired and is no longer valid.</span></div>
      </div>
    )
  }

  const key = String(new Date())
  const hasTimeLimit = isNotNil(expirationTime) && isNotNil(timezone)
  const timeLimit = hasTimeLimit ? formatLongDateTime(expirationTime as Date, timezone as string) : ''

  return (
    <div>
      <Menu borderless>
        <Menu.Item>
          <If isTrue={ !shouldRenderReskin2025 }>
            <SafeImage
              size="small"
              src="/asset/image/curation-logo-no-tag.png"
            />
          </If>
          <If isTrue={ shouldRenderReskin2025 }>
            <ReveleerHeaderLogo />
          </If>
        </Menu.Item>
      </Menu>
      <div className="header-padding">
        <Container>
          <SafeHeader
            as="h4"
            attached="top"
            block
            className={
              `main-schedule-header${shouldRenderReskin2025 ? ' reskin' : ''}`
            }
          >
            <If isTrue={ !shouldRenderReskin2025 }>
              <Icon name="lock" />
            </If>
            <If isTrue={ shouldRenderReskin2025 }>
              <span className="m-8px-right">
                <LockVersionTwoLocked />
              </span>
            </If>
            &nbsp;Reset Password
          </SafeHeader>
          <NotificationContainer />
          <SafeSegment
            attached
            className="no-margin"
          >
            <SafeGrid stackable>
              <Grid.Row>
                <Grid.Column
                  mobile={ 16 }
                  tablet={ 16 }
                  computer={ 16 }
                >
                  <UserPasswordChange
                    key={ key }
                    passwordChange={ passwordChange }
                    errors={ errors }
                    setErrors={ setErrors }
                    onSave={ onSavePasswordChange }
                  />
                </Grid.Column>
              </Grid.Row>
              <If isTrue={ hasTimeLimit }>
                <Grid.Row>
                  <Grid.Column
                    mobile={ 16 }
                    tablet={ 16 }
                    computer={ 16 }
                  >
                    <span>
                      { `Submissions will be accepted until ${timeLimit}` }
                    </span>
                  </Grid.Column>
                </Grid.Row>
              </If>
            </SafeGrid>
          </SafeSegment>
        </Container>
      </div>
    </div>
  )
}

export default withRouter(connect(
  () => ({}),
  {
    doResetPassword
  }
)(Reset))
